// Chemical / Inventory API wrappers.

import type {
  ActiveRei,
  Application,
  FracRotationApplication,
  FracRotationGroup,
  Product,
  ProductCategory,
} from '@greeniq/shared';
import { api } from './client.js';

export interface ProductsResponse {
  products: Product[];
}

export function listProducts(
  courseSlug: string,
  opts: { category?: ProductCategory; q?: string; limit?: number } = {},
): Promise<ProductsResponse> {
  const params = new URLSearchParams();
  if (opts.category) params.set('category', opts.category);
  if (opts.q) params.set('q', opts.q);
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return api.get<ProductsResponse>(`/courses/${courseSlug}/products${qs ? `?${qs}` : ''}`);
}

export function getProduct(courseSlug: string, productId: number): Promise<{ product: Product }> {
  return api.get<{ product: Product }>(`/courses/${courseSlug}/products/${productId}`);
}

export interface ApplicationsResponse {
  applications: Application[];
}

export function listApplications(
  courseSlug: string,
  opts: { zoneId?: number; limit?: number } = {},
): Promise<ApplicationsResponse> {
  const params = new URLSearchParams();
  if (opts.zoneId !== undefined) params.set('zoneId', String(opts.zoneId));
  if (opts.limit !== undefined) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return api.get<ApplicationsResponse>(`/courses/${courseSlug}/applications${qs ? `?${qs}` : ''}`);
}

export interface NewApplicationInput {
  productId: number;
  zoneId?: number | null;
  appliedAt: string;
  ratePer1000sqft?: number;
  rateUnit?: string;
  areaSqft: number;
  totalProductUsed?: number;
  totalProductUnit?: string;
  waterCarrierGal?: number;
  notes?: string;
  weatherSnapshot?: Record<string, unknown>;
}

export function createApplication(
  courseSlug: string,
  input: NewApplicationInput,
): Promise<{ application: Application }> {
  return api.post<{ application: Application }>(
    `/courses/${courseSlug}/applications`,
    input,
  );
}

export interface ActiveReisResponse {
  reis: ActiveRei[];
}

export function getActiveReis(courseSlug: string): Promise<ActiveReisResponse> {
  return api.get<ActiveReisResponse>(`/courses/${courseSlug}/applications/active-reis`);
}

export function signageUrl(courseSlug: string, applicationId: number): string {
  return `/api/courses/${courseSlug}/applications/${applicationId}/signage`;
}

export interface FracRotationResponse {
  applications: FracRotationApplication[];
  groups: FracRotationGroup[];
}

export function getFracRotation(
  courseSlug: string,
  zoneId: number,
  days = 180,
): Promise<FracRotationResponse> {
  return api.get<FracRotationResponse>(
    `/courses/${courseSlug}/zones/${zoneId}/frac-rotation?days=${days}`,
  );
}

export interface BulkImportRowPreview {
  rowNumber: number;
  epaRegNo: string | null;
  name: string;
  action: 'insert' | 'update' | 'skip' | 'error';
  reason?: string;
  existingProductId?: number;
}

export interface BulkImportResponse {
  mode: 'preview' | 'commit';
  headerWarnings: string[];
  totalRows: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: number;
  previews: BulkImportRowPreview[];
}

export function importProductsCsv(
  courseSlug: string,
  csv: string,
  mode: 'preview' | 'commit',
): Promise<BulkImportResponse> {
  return api.post<BulkImportResponse>(
    `/courses/${courseSlug}/products/import`,
    { csv, mode },
  );
}
